import React, { useMemo, useState } from "react";
import type { Editor } from "@tiptap/react";
import { Braces, Search } from "lucide-react";
import { useToast } from "@/context/ToastContext";
import type { MergeField } from "@/types/proposal";

const GROUPS: Record<string, string> = {
  customer: "Customer",
  contact: "Contact",
  deal: "Deal",
  qualification: "Qualification",
  owner: "Sales rep",
  proposal: "Proposal",
  company: "Your company",
};

/**
 * Merge fields grouped by what they come from. Clicking one inserts {{field}} at the cursor of the
 * section last clicked in; with no section focused it is copied instead.
 */
export function MergeFieldPanel({
  fields,
  activeEditor,
  disabled,
  showValues = true,
}: {
  fields: MergeField[];
  activeEditor: React.MutableRefObject<Editor | null>;
  disabled?: boolean;
  showValues?: boolean;
}) {
  const { success } = useToast();
  const [q, setQ] = useState("");

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const out = new Map<string, MergeField[]>();
    for (const f of fields) {
      if (needle && !`${f.key} ${f.label} ${f.value || ""}`.toLowerCase().includes(needle)) continue;
      const g = f.key.split(".")[0] || f.key;
      out.set(g, [...(out.get(g) || []), f]);
    }
    return [...out.entries()];
  }, [fields, q]);

  const insert = async (key: string) => {
    const token = `{{${key}}}`;
    const ed = activeEditor.current;
    if (ed && !ed.isDestroyed && ed.isEditable && !disabled) {
      ed.chain().focus().insertContent(token).run();
      return;
    }
    try {
      await navigator.clipboard.writeText(token);
      success("Copied", `${token} copied. Click inside a section and paste it, or click a section first and pick the field again.`);
    } catch {
      success("Merge field", `Type ${token} where you want it`);
    }
  };

  return (
    <div className="bg-card border rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <Braces className="size-4 text-primary" />
          <h3 className="text-sm font-bold">Merge fields</h3>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1">
          Click inside a section, then pick a field to insert it. It's filled in for the customer.
        </p>
        <div className="relative mt-2">
          <Search className="size-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find a field"
            className="h-8 w-full rounded-md border border-input bg-background pl-8 pr-2 text-xs"
          />
        </div>
      </div>
      <div className="max-h-[420px] overflow-y-auto py-1">
        {groups.length === 0 && <div className="px-4 py-3 text-xs text-muted-foreground">No matching fields</div>}
        {groups.map(([g, list]) => (
          <div key={g} className="py-1">
            <div className="px-4 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{GROUPS[g] || g}</div>
            {list.map((f) => (
              <button
                key={f.key}
                type="button"
                // keep the section's cursor where it is
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => insert(f.key)}
                className="w-full text-left px-4 py-1.5 hover:bg-muted/60 group"
                title={`Insert {{${f.key}}}`}
              >
                <div className="text-xs font-medium text-foreground">{f.label}</div>
                {showValues && (
                  <div className={f.value ? "text-[11px] text-muted-foreground truncate" : "text-[11px] text-warning truncate"}>
                    {f.value || "empty for this deal"}
                  </div>
                )}
                <code className="text-[10px] text-primary/80 hidden group-hover:block">{`{{${f.key}}}`}</code>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
